<template>
  <form v-if="newUiLayout" :id="formId" class="recipient-form" @submit.prevent="onSubmit">
    <section class="recipient-form__section" :aria-labelledby="`${formId}-personal`">
      <h3 :id="`${formId}-personal`" class="recipient-form__title">
        {{ $t("Personal data") }}
      </h3>

      <div class="recipient-form__grid">
        <UiField
          :label="$t('First name')"
          :for-id="`${formId}-first-name`"
          :error="fieldError('fullNameData.firstName')"
        >
          <UiInput
            :id="`${formId}-first-name`"
            v-model="childData.firstName"
            :aria-invalid="invalidAttr('fullNameData.firstName')"
            :placeholder="$t('Enter first name')"
            required
            minlength="2"
            autofocus
            @update:model-value="clearFieldError('fullNameData.firstName')"
          />
        </UiField>

        <UiField
          :label="$t('Last name')"
          :for-id="`${formId}-last-name`"
          :error="fieldError('fullNameData.lastName')"
        >
          <UiInput
            :id="`${formId}-last-name`"
            v-model="childData.lastName"
            :aria-invalid="invalidAttr('fullNameData.lastName')"
            :placeholder="$t('Enter last name')"
            required
            minlength="2"
            @update:model-value="clearFieldError('fullNameData.lastName')"
          />
        </UiField>
      </div>
    </section>

    <section class="recipient-form__section" :aria-labelledby="`${formId}-institution`">
      <h3 :id="`${formId}-institution`" class="recipient-form__title">
        {{ $t("Institution and diet") }}
      </h3>

      <InstitutionsHideableSelect
        v-model:selected-institution="selectedInstitution"
        :mount-mode="mountMode"
        :default-institution-id="defaultInstitutionId"
        @update:selected-institution="onInstitutionChange"
        @clear-institution-related-data="clearInstitutionRelatedData"
      />

      <div class="recipient-form__grid">
        <div
          v-if="canChangeClassroom"
          class="ui-field"
          :class="{ 'is-invalid': fieldError('classroomId') }"
        >
          <Multiselector
            id="classroom-selector"
            :options="classroomOptions"
            :model-value="[childData.classroomId]"
            :max-height="300"
            :multiple="false"
            :disabled="!selectedInstitution"
            :placeholder="selectedInstitution ? undefined : $t('Select institution first')"
            :main-input-label="$t('Classroom')"
            @update:model-value="onClassroomSelect"
          />

          <p v-if="fieldError('classroomId')" class="ui-field__error" role="alert">
            {{ fieldError("classroomId") }}
          </p>
        </div>

        <ChildrenGroupsSelect v-if="showGroups" v-model="childData.childGroupId" />

        <div v-if="canChangeDiet" class="ui-field" :class="{ 'is-invalid': fieldError('dietId') }">
          <DietsSelect
            v-model="childData.dietId"
            :label="$t('Diet')"
            :auto-select-default="true"
            :institution-id="selectedInstitution"
            :disabled="!selectedInstitution"
            :disabled-placeholder="$t('Select institution first')"
            :disable-placeholder="true"
            placeholder="select"
            @update:model-value="clearFieldError('dietId')"
          />

          <p v-if="fieldError('dietId')" class="ui-field__error" role="alert">
            {{ fieldError("dietId") }}
          </p>
        </div>
      </div>

      <UiAlert v-if="selectedDietDescription">{{ selectedDietDescription }}</UiAlert>

      <Multiselector
        v-if="canModifyPriceListsGroup && priceListsGroupsOptions.length > 0"
        id="price-lists-groups-select"
        :model-value="childData.priceListsGroupId"
        :options="priceListsGroupsOptions"
        :placeholder="$t('Select')"
        :main-input-label="$t('Price lists group')"
        :multiple="false"
        :show-search="false"
        @update:model-value="onPriceListsGroupChange"
      />

      <template v-if="canChangeAllergensForCurrentDiet">
        <template v-if="canChangeAllergens && allergens">
          <Multiselector
            id="allergens-selector"
            :model-value="defaultAllergensValues"
            :options="allergensOptions"
            :placeholder="$t('Choose allergens')"
            :main-input-label="$t('Allergens and dietary exceptions')"
            :tooltip="
              $t(
                'Please mark dietary exceptions ONLY if recipient of meals is allergic and cannot consume products containing selected allergens.',
              )
            "
            @update:model-value="onChangeSelectedAllergens"
          />

          <UiAlert v-if="showExtraAllergensInfo" tone="warning">
            {{
              $t(
                "Please mark dietary exceptions ONLY if recipient of meals is allergic and cannot consume products containing selected allergens.",
              )
            }}
          </UiAlert>
        </template>

        <UiField
          v-if="canChangeAllergensNote"
          :label="$t('Allergies note')"
          :for-id="`${formId}-allergies-note`"
          :hint="$t('Optional field')"
        >
          <textarea
            :id="`${formId}-allergies-note`"
            v-model="childData.allergiesNote"
            class="ui-input recipient-form__textarea"
            rows="3"
            :placeholder="$t('Here you can enter allergies note.')"
          />
        </UiField>
      </template>

      <UiField
        v-if="canEditDiningCard"
        :label="$t('Dinner card number')"
        :for-id="`${formId}-card-number`"
        :error="fieldError('cardNumber')"
      >
        <UiAlert
          v-if="addCardsFee && ((firstCardFee?.amount ?? 0) > 0 || (nextCardFee?.amount ?? 0) > 0)"
          tone="warning"
        >
          {{ $t("PLEASE NOTE: Changing your meal card number will incur a fee.") }}
          <br />
          {{ $t("First card fee") }}:
          <strong>{{ formatMoney(firstCardFee ?? { amount: 0 }) }}</strong
          ><br />
          {{ $t("Next card fee") }}:
          <strong>{{ formatMoney(nextCardFee ?? { amount: 0 }) }}</strong>
        </UiAlert>

        <UiInput
          :id="`${formId}-card-number`"
          v-model="childData.cardNumber"
          :aria-invalid="invalidAttr('cardNumber')"
          :placeholder="$t('Enter card number')"
          :pattern="cardNumberInputPattern"
          :maxlength="cardNumberLength"
          @update:model-value="clearFieldError('cardNumber')"
        />

        <p class="ui-field__hint">
          {{
            $t(
              "Optional field, card number is required to receive meals, can be completed in the future.",
            )
          }}
        </p>

        <UiAlert v-if="$te('Order a card')" tone="warning">
          {{ $t("Order a card") }}
        </UiAlert>
      </UiField>
    </section>

    <section
      v-if="showNote"
      class="recipient-form__section"
      :aria-labelledby="`${formId}-delivery`"
    >
      <h3 :id="`${formId}-delivery`" class="recipient-form__title">
        {{ $t("Delivery and contact") }}
      </h3>

      <UiField
        :label="$t('Delivery address, contact number')"
        :for-id="`${formId}-general-note`"
        :hint="$t('Optional field')"
        :error="fieldError('generalNote')"
      >
        <textarea
          :id="`${formId}-general-note`"
          v-model="childData.generalNote"
          :aria-invalid="invalidAttr('generalNote')"
          class="ui-input recipient-form__textarea"
          rows="3"
          :placeholder="$t('Here you can enter your delivery address and contact number')"
          @update:model-value="clearFieldError('generalNote')"
        />
      </UiField>
    </section>

    <section
      v-if="isAdmin || enableSubscribedPaymentsField || canSeeDiscount || canSeePrepaidsOptions"
      class="recipient-form__section"
      :aria-labelledby="`${formId}-admin`"
    >
      <h3 :id="`${formId}-admin`" class="recipient-form__title">
        {{ $t("Administrative settings") }}
      </h3>

      <UiField v-if="isAdmin" :label="$t('Internal note')" :for-id="`${formId}-internal-note`">
        <textarea
          :id="`${formId}-internal-note`"
          v-model="childData.internalNote"
          class="ui-input recipient-form__textarea"
          rows="4"
          :placeholder="$t('Enter note')"
        />
      </UiField>

      <UiModalSection
        v-if="enableSubscribedPaymentsField || canSeeDiscount || canSeePrepaidsOptions"
      >
        <UiModalCard v-if="enableSubscribedPaymentsField">
          <UiSwitch v-model="childData.subscribedPayments" class="recipient-form__switch">
            {{ $t("Is subscribed buyer") }}
          </UiSwitch>
        </UiModalCard>

        <UiModalCard v-if="canSeeDiscount">
          <UiSwitch v-model="childData.hasDiscount" class="recipient-form__switch">
            {{ $t("Set discount") }}
          </UiSwitch>

          <div v-if="childData.hasDiscount" class="recipient-form__card-body">
            <div class="recipient-form__grid">
              <UiField
                :label="$t('Discount percent')"
                :for-id="`${formId}-discount-percent`"
                :error="fieldError('discountPercent')"
              >
                <UiInput
                  :id="`${formId}-discount-percent`"
                  v-model="childData.discountPercent"
                  :aria-invalid="invalidAttr('discountPercent')"
                  type="number"
                  min="0"
                  max="100"
                  :placeholder="$t('Enter discount percent')"
                  @update:model-value="clearFieldError('discountPercent')"
                />
              </UiField>

              <UiField
                :label="$t('Discount expiration date')"
                :for-id="`${formId}-discount-expiration-date`"
                :hint="$t('Optional field')"
                :error="fieldError('expirationDate')"
              >
                <UiDateInput
                  :id="`${formId}-discount-expiration-date`"
                  v-model="childData.expirationDate"
                  :aria-invalid="invalidAttr('expirationDate')"
                  @update:model-value="clearFieldError('expirationDate')"
                />
              </UiField>
            </div>
          </div>
        </UiModalCard>

        <UiModalCard v-if="canSeePrepaidsOptions">
          <UiSwitch v-model="childData.orderingPrepaids" class="recipient-form__switch">
            {{ $t("Ordering prepaids") }}
          </UiSwitch>
        </UiModalCard>
      </UiModalSection>
    </section>

    <section
      v-if="canEditSubjectNotificationsSettings || showAutomarking"
      class="recipient-form__section"
      :aria-labelledby="`${formId}-automation`"
    >
      <h3 :id="`${formId}-automation`" class="recipient-form__title">
        {{ $t("Notifications and automation") }}
      </h3>

      <UiModalSection>
        <UiModalCard v-if="canEditSubjectNotificationsSettings">
          <UiSwitch
            v-model="childData.subjectNotificationsSettings.missingOrdersEnabled"
            class="recipient-form__switch"
          >
            {{ $t("Enable missing orders notifications") }}
          </UiSwitch>

          <p class="recipient-form__switch-description">
            {{ $t("We will let you know when there is no order for the coming days.") }}
          </p>

          <div
            v-if="childData.subjectNotificationsSettings.missingOrdersEnabled"
            class="recipient-form__card-body"
          >
            <div class="recipient-form__grid">
              <UiField
                :label="$t('How many days before we should notify?')"
                :for-id="`${formId}-missing-orders-days-before`"
                :error="fieldError('subjectNotificationsSettingsData.missingOrdersDaysBefore')"
              >
                <UiInput
                  :id="`${formId}-missing-orders-days-before`"
                  v-model="childData.subjectNotificationsSettings.missingOrdersDaysBefore"
                  :aria-invalid="
                    invalidAttr('subjectNotificationsSettingsData.missingOrdersDaysBefore')
                  "
                  type="number"
                  min="1"
                  step="1"
                  @update:model-value="
                    clearFieldError('subjectNotificationsSettingsData.missingOrdersDaysBefore')
                  "
                />
              </UiField>

              <UiField
                :label="$t('At which hour we should notify?')"
                :for-id="`${formId}-missing-orders-trigger-time`"
                :error="fieldError('subjectNotificationsSettingsData.missingOrdersTriggerTime')"
              >
                <UiInput
                  :id="`${formId}-missing-orders-trigger-time`"
                  v-model="childData.subjectNotificationsSettings.missingOrdersTriggerTime"
                  :aria-invalid="
                    invalidAttr('subjectNotificationsSettingsData.missingOrdersTriggerTime')
                  "
                  type="time"
                  @update:model-value="
                    clearFieldError('subjectNotificationsSettingsData.missingOrdersTriggerTime')
                  "
                />
              </UiField>
            </div>
          </div>
        </UiModalCard>

        <template v-if="showAutomarking">
          <UiModalCard>
            <UiSwitch
              v-model="childData.calendarSubjectSettings.automarkPresences"
              class="recipient-form__switch"
            >
              {{ $t("Automark presences") }}
            </UiSwitch>

            <p class="recipient-form__switch-description">
              {{ $t("Meals are marked automatically on the selected weekdays.") }}

              <button
                type="button"
                class="recipient-form__link"
                @click="onHowAutomarkingWorksClick"
              >
                {{ $t("How automarking works?") }}
              </button>
            </p>

            <button
              v-if="calendarSubjectSettingsLogs?.length > 0"
              type="button"
              class="ui-action-pill recipient-form__history"
              @click.prevent.stop="onCalendarSubjectSettingsHistoryClick"
            >
              <HistoryIcon :size="18" :stroke-width="2" aria-hidden="true" />

              <span class="ui-action-pill__label">{{ $t("Automarking settings history") }}</span>
            </button>

            <div
              v-if="childData.calendarSubjectSettings.automarkPresences"
              class="recipient-form__card-body"
            >
              <UiAlert v-if="itemsGroupedByMealTypeCategories.length === 0">
                {{
                  $t(
                    "First select institution, classroom and diet to see list of autoselected meal types.",
                  )
                }}
              </UiAlert>

              <UiWeekdayPicker
                v-model="childData.calendarSubjectSettings.automarkedWeekdays"
                :label="$t('Weekdays')"
              />

              <div class="recipient-form__grid">
                <UiField
                  :label="$t('Date from')"
                  :for-id="`${formId}-automark-start-date`"
                  :error="fieldError('calendarSubjectSettingsData.automarkStartDate')"
                >
                  <UiDateInput
                    :id="`${formId}-automark-start-date`"
                    v-model="childData.calendarSubjectSettings.automarkStartDate"
                    :aria-invalid="invalidAttr('calendarSubjectSettingsData.automarkStartDate')"
                    @update:model-value="
                      clearFieldError('calendarSubjectSettingsData.automarkStartDate')
                    "
                  />
                </UiField>

                <UiField
                  :label="$t('Date to')"
                  :for-id="`${formId}-automark-end-date`"
                  :error="fieldError('calendarSubjectSettingsData.automarkEndDate')"
                >
                  <UiDateInput
                    :id="`${formId}-automark-end-date`"
                    v-model="childData.calendarSubjectSettings.automarkEndDate"
                    :aria-invalid="invalidAttr('calendarSubjectSettingsData.automarkEndDate')"
                    @update:model-value="
                      clearFieldError('calendarSubjectSettingsData.automarkEndDate')
                    "
                  />
                </UiField>

                <UiField
                  v-if="autoselectionIntervalOptions.length > 0"
                  :label="$t('Autoselection interval')"
                >
                  <UiSelect
                    v-model="childData.calendarSubjectSettings.autoselectionInterval"
                    :options="autoselectionIntervalOptions"
                    :placeholder="$t('Select')"
                  />
                </UiField>
              </div>
            </div>
          </UiModalCard>

          <AutomarkingMealTypesPicker
            v-if="
              childData.calendarSubjectSettings.automarkPresences &&
              itemsGroupedByMealTypeCategories.length > 0
            "
            :categories="itemsGroupedByMealTypeCategories"
            :selection="childData.calendarSubjectSettings.autoselectedPriceListsItemsIds"
            @select="onAutoselectedMealTypeSelect"
          />
        </template>
      </UiModalSection>
    </section>

    <FormActions
      v-if="variant !== 'modal'"
      :submit-action="onSubmit"
      :back-action="onGoBackButtonClick"
      :is-edit="isEditMode"
      :show-back-button="variant === 'default'"
      :custom-submit-text="isEditMode ? 'Save' : 'Create'"
      :custom-submit-icon="isEditMode ? 'fa-save' : 'fa-plus'"
      :justify-content-class="variant === 'default' ? 'between' : 'end'"
      :ready="isChildDataReady"
    />

    <AutomarkingInfoModal
      :show="showAutomarkingInfoModal"
      variant="edit"
      @hide="onAutomarkingInfoModalHide"
    />

    <AutomarkingActivationConfirmModal
      v-if="showAutomarkingActivationConfirmModal"
      @confirm="onAutomarkingActivationConfirmed"
      @cancel="onAutomarkingActivationCancelled"
      @show-info-click="onHowAutomarkingWorksClick"
    />

    <CalendarSubjectSettingsLogModal
      :show="showCalendarSubjectSettingsLogModal"
      :calendar-subject-settings-logs="calendarSubjectSettingsLogs"
      @hide="onCalendarSubjectSettingsLogModalHide"
    />
  </form>

  <b-form v-else id="parent-child-edit-form" class="mb-3" @submit.prevent="onSubmit">
    <!-- First name -->
    <b-form-group>
      <label for="first-name-input">{{ $t("First name") }}</label>

      <b-input
        id="first-name-input"
        v-model="childData.firstName"
        type="text"
        :placeholder="$t('Enter first name')"
        required
        minlength="2"
        autofocus
      />
    </b-form-group>

    <!-- Last name -->
    <b-form-group>
      <label for="last-name-input">{{ $t("Last name") }}</label>

      <b-input
        id="last-name-input"
        v-model="childData.lastName"
        type="text"
        :placeholder="$t('Enter last name')"
        required
        minlength="2"
      />
    </b-form-group>

    <RegistrationInstitutionSelect
      v-if="isRegister"
      v-model="selectedInstitution"
      registration-user-type="parent"
      :locked-institution-id="defaultInstitutionId"
      :locked-institution-name="defaultInstitutionName"
      @update:model-value="onInstitutionChange"
    />

    <InstitutionsHideableSelect
      v-else
      v-model:selected-institution="selectedInstitution"
      :mount-mode="mountMode"
      :default-institution-id="defaultInstitutionId"
      @update:selected-institution="onInstitutionChange"
      @clear-institution-related-data="clearInstitutionRelatedData"
    />

    <!-- Classroom -->
    <b-form-group v-if="canChangeClassroom">
      <Multiselector
        id="classroom-selector"
        :options="classroomOptions"
        :model-value="[childData.classroomId]"
        :max-height="300"
        :multiple="false"
        :disabled="!selectedInstitution"
        :main-input-label="$t('Classroom')"
        @update:model-value="onChangeClassroom"
      />
    </b-form-group>

    <!-- Child group -->
    <b-form-group v-if="showGroups">
      <ChildrenGroupsSelect v-model="childData.childGroupId" />
    </b-form-group>

    <!-- Card number -->
    <b-form-group v-if="canEditDiningCard">
      <label for="card-number-input">
        {{ $t("Dinner card number") }}
        <Tooltip
          :value="
            $t(
              'Optional field, card number is required to receive meals, can be completed in the future.',
            )
          "
        />
      </label>

      <div
        v-if="addCardsFee && ((firstCardFee?.amount ?? 0) > 0 || (nextCardFee?.amount ?? 0) > 0)"
        class="alert alert-warning"
      >
        {{ $t("PLEASE NOTE: Changing your meal card number will incur a fee.") }}
        <br />
        {{ $t("First card fee") }}: <strong>{{ formatMoney(firstCardFee ?? { amount: 0 }) }}</strong
        ><br />
        {{ $t("Next card fee") }}: <strong>{{ formatMoney(nextCardFee ?? { amount: 0 }) }}</strong>
      </div>

      <input
        id="card-number-input"
        v-model="childData.cardNumber"
        class="form-control"
        type="text"
        :placeholder="$t('Enter card number')"
        :pattern="cardNumberInputPattern"
        :maxlength="cardNumberLength"
      />

      <b-alert :model-value="$te('Order a card')" variant="warning" class="mb-0 mt-3">
        <span>{{ $t("Order a card") }}</span>
      </b-alert>
    </b-form-group>

    <!-- Diet -->
    <b-form-group v-if="canChangeDiet">
      <DietsSelect
        v-model="childData.dietId"
        :auto-select-default="true"
        :institution-id="selectedInstitution"
        :default-options="isRegister ? registerDietsOptions : null"
        :disabled="!selectedInstitution"
        :disabled-placeholder="$t('Select institution first')"
        :disable-placeholder="true"
        placeholder="select"
      />
    </b-form-group>

    <b-alert :model-value="selectedDietDescription" variant="warning" class="mt-3">
      {{ selectedDietDescription }}
    </b-alert>

    <!-- Price lists group -->
    <Multiselector
      v-if="canModifyPriceListsGroup && priceListsGroupsOptions.length > 0"
      id="price-lists-groups-select"
      :model-value="childData.priceListsGroupId"
      :options="priceListsGroupsOptions"
      :placeholder="$t('Select')"
      :main-input-label="$t('Price lists group')"
      :multiple="false"
      :show-search="false"
      @update:model-value="onPriceListsGroupChange"
    />

    <!-- Allergens -->
    <template v-if="canChangeAllergensForCurrentDiet">
      <b-form-group v-if="canChangeAllergens && allergens" class="mb-0">
        <Multiselector
          id="allergens-selector"
          :model-value="defaultAllergensValues"
          :options="allergensOptions"
          :placeholder="$t('Choose allergens')"
          :main-input-label="$t('Allergens and dietary exceptions')"
          :tooltip="
            $t(
              'Please mark dietary exceptions ONLY if recipient of meals is allergic and cannot consume products containing selected allergens.',
            )
          "
          @update:model-value="onChangeSelectedAllergens"
        />

        <b-alert :model-value="showExtraAllergensInfo" variant="warning" class="mt-3">
          {{
            $t(
              "Please mark dietary exceptions ONLY if recipient of meals is allergic and cannot consume products containing selected allergens.",
            )
          }}
        </b-alert>
      </b-form-group>

      <!-- Allergens note -->
      <b-form-group v-if="canChangeAllergensNote">
        <label for="allergens-note-textarea"
          >{{ $t("Allergies note") }}
          <Tooltip :value="$t('Optional field')" />
        </label>

        <b-form-textarea
          id="allergens-note-textarea"
          v-model="childData.allergiesNote"
          rows="3"
          :placeholder="$t('Here you can enter allergies note.')"
        />
      </b-form-group>
    </template>

    <!-- Delivery address, contact number -->
    <b-form-group v-if="showNote">
      <label for="general-note-textarea"
        >{{ $t("Delivery address, contact number") }}
        <Tooltip :value="$t('Optional field')" />
      </label>

      <b-form-textarea
        id="general-note-textarea"
        v-model="childData.generalNote"
        rows="3"
        :placeholder="$t('Here you can enter your delivery address and contact number')"
      />
    </b-form-group>

    <!-- Internal note -->
    <b-form-group v-if="isAdmin">
      <label for="child-internal-note">{{ $t("Internal note") }}</label>

      <b-form-textarea
        id="child-internal-note"
        v-model="childData.internalNote"
        :placeholder="$t('Enter note')"
        rows="4"
      >
        {{ childData.internalNote }}
      </b-form-textarea>
    </b-form-group>

    <!-- Subscribed payments -->
    <b-form-group v-if="enableSubscribedPaymentsField" class="d-flex mb-3" sm="12">
      <b-form-checkbox
        id="checkbox-subscribed-payments"
        v-model="childData.subscribedPayments"
        switch
      >
        {{ $t("Is subscribed buyer") }}
      </b-form-checkbox>
    </b-form-group>

    <!-- Discount -->
    <b-form-group v-if="canSeeDiscount" class="mb-3">
      <b-form-checkbox id="checkbox-has-discount" v-model="childData.hasDiscount" switch>
        {{ $t("Set discount") }}
      </b-form-checkbox>

      <b-collapse :show="childData.hasDiscount" class="mt-3">
        <b-form-group>
          <label for="input-discount-percent">{{ $t("Discount percent") }}</label>

          <b-input-group>
            <b-input
              id="input-discount-percent"
              v-model="childData.discountPercent"
              type="number"
              min="0"
              max="100"
              :placeholder="$t('Enter discount percent')"
            />

            <div class="input-group-text">%</div>
          </b-input-group>
        </b-form-group>

        <b-form-group>
          <label for="discount-expiration-date">
            {{ $t("Discount expiration date") }}
            <Tooltip :value="$t('Optional field')" />
          </label>

          <b-form-input
            id="discount-expiration-date"
            v-model="childData.expirationDate"
            type="date"
          />
        </b-form-group>
      </b-collapse>
    </b-form-group>

    <b-form-group v-if="canSeePrepaidsOptions">
      <b-form-checkbox id="checkbox-ordering-prepaids" v-model="childData.orderingPrepaids" switch>
        {{ $t("Ordering prepaids") }}
      </b-form-checkbox>
    </b-form-group>

    <b-form-group v-if="canEditSubjectNotificationsSettings">
      <b-form-group>
        <b-form-checkbox
          id="checkbox-missing-orders-enabled"
          v-model="childData.subjectNotificationsSettings.missingOrdersEnabled"
          switch
        >
          {{ $t("Enable missing orders notifications") }}
        </b-form-checkbox>
      </b-form-group>

      <b-collapse :show="childData.subjectNotificationsSettings.missingOrdersEnabled">
        <b-form-group>
          <label>{{ $t("How many days before we should notify?") }}</label>

          <b-input
            v-model="childData.subjectNotificationsSettings.missingOrdersDaysBefore"
            type="number"
            min="1"
            step="1"
          />
        </b-form-group>

        <b-form-group>
          <label for="missing-orders-trigger-time-input">{{
            $t("At which hour we should notify?")
          }}</label>

          <b-input
            id="missing-orders-trigger-time-input"
            v-model="childData.subjectNotificationsSettings.missingOrdersTriggerTime"
            type="time"
          />
        </b-form-group>
      </b-collapse>
    </b-form-group>

    <!-- Automarking -->
    <div v-if="showAutomarking" class="mb-3">
      <div class="mb-3">
        <AutomarkingInfoButton @click="onHowAutomarkingWorksClick" />
      </div>

      <div class="mb-3">
        <button
          v-if="calendarSubjectSettingsLogs?.length > 0"
          type="button"
          class="button-with-outline py-2 px-3"
          :title="$t('Automarking settings history')"
          @click.prevent.stop="onCalendarSubjectSettingsHistoryClick"
        >
          <UiIcon fa="fa fa-history" /> {{ $t("Automarking settings history") }}
        </button>
      </div>

      <!-- Automark presences -->
      <div v-if="!isRegister" class="mb-3">
        <b-form-checkbox
          id="checkbox-automark-presences"
          v-model="childData.calendarSubjectSettings.automarkPresences"
          switch
        >
          {{ $t("Automark presences") }}
        </b-form-checkbox>
      </div>

      <b-collapse v-model="childData.calendarSubjectSettings.automarkPresences">
        <template v-if="!isRegister">
          <!-- Automarked weekdays -->
          <div class="mb-3">
            <label for="automarked-weekdays-checkboxes">{{ $t("Automarked weekdays") }}</label>

            <b-form-group id="automarked-weekdays-checkboxes">
              <b-form-checkbox
                v-for="(automarkedWeekday, automarkedWeekdayIndex) in childData
                  .calendarSubjectSettings.automarkedWeekdays"
                :key="automarkedWeekdayIndex"
                v-model="
                  childData.calendarSubjectSettings.automarkedWeekdays[automarkedWeekdayIndex]
                "
              >
                {{ $t(`automarked_weekday_${automarkedWeekdayIndex}`) }}
              </b-form-checkbox>
            </b-form-group>
          </div>

          <div class="mb-3">
            <label for="automark-start-date">{{ $t("Automark start date") }}</label>

            <b-form-group id="automark-start-date">
              <b-input v-model="childData.calendarSubjectSettings.automarkStartDate" type="date" />
            </b-form-group>
          </div>

          <div class="mb-3">
            <label for="automark-start-date">{{ $t("Automark end date") }}</label>

            <b-form-group id="automark-start-date">
              <b-input v-model="childData.calendarSubjectSettings.automarkEndDate" type="date" />
            </b-form-group>
          </div>

          <!-- Autoselection interval -->
          <Multiselector
            v-if="autoselectionIntervalOptions.length > 0"
            id="autoselection-interval-select"
            :model-value="childData.calendarSubjectSettings.autoselectionInterval"
            :options="autoselectionIntervalOptions"
            :placeholder="$t('Select')"
            :main-input-label="$t('Autoselection interval')"
            :multiple="false"
            :show-search="false"
            @update:model-value="
              (value) => (childData.calendarSubjectSettings.autoselectionInterval = value)
            "
          />
        </template>

        <!-- Autoselected meal types -->
        <b-form-group>
          <template v-if="itemsGroupedByMealTypeCategories.length > 0">
            <h5 class="text-uppercase fw-bold mt-3">
              {{ $t("Enable automatic ordering") }}
            </h5>

            <div
              v-for="(category, categoryIndex) in itemsGroupedByMealTypeCategories"
              :key="`category-${categoryIndex}`"
            >
              <p class="mb-2 mt-3 fw-bold">
                {{ category.name }}
              </p>

              <table>
                <!-- Meal types -->
                <template
                  v-for="(mealType, mealTypeKey) in category.mealTypes"
                  :key="`mealType-${mealTypeKey}`"
                >
                  <tr>
                    <td colspan="2" class="ps-2 pt-2">
                      <template
                        v-if="
                          Object.values(mealType.portionSizes).length === 1 &&
                          Object.values(mealType.portionSizes)[0].priceListItem
                        "
                      >
                        <b-form-checkbox
                          v-model="
                            childData.calendarSubjectSettings.autoselectedPriceListsItemsIds[
                              mealType.id
                            ]
                          "
                          :value="Object.values(mealType.portionSizes)[0].priceListItem.id"
                          :unchecked-value="null"
                          :disabled="isRegister ? mealType.blockOnRegistration : false"
                        >
                          {{ mealType.name }}

                          <Tooltip v-if="mealType.description" :value="mealType.description" />

                          <HiddenPriceListItemBadge
                            v-if="
                              shouldShowHiddenPriceListItemBadge(
                                Object.values(mealType.portionSizes)[0].priceListItem,
                              )
                            "
                            :price-list-item="Object.values(mealType.portionSizes)[0].priceListItem"
                          />

                          <strong class="ms-3">{{
                            formatMoney(Object.values(mealType.portionSizes)[0].priceListItem.price)
                          }}</strong>
                        </b-form-checkbox>
                      </template>

                      <template v-else>
                        {{ mealType.name }}

                        <Tooltip v-if="mealType.description" :value="mealType.description" />
                      </template>
                    </td>
                  </tr>

                  <template v-if="Object.values(mealType.portionSizes).length > 1">
                    <!-- Portion sizes -->
                    <template
                      v-for="(portionSize, portionSizeKey) in mealType.portionSizes"
                      :key="`portionSize-${mealTypeKey}-${portionSizeKey}`"
                    >
                      <tr v-if="portionSize.priceListItem">
                        <td class="ps-4">
                          <!-- Radio buttons to select price list item id -->
                          <b-form-checkbox
                            v-model="
                              childData.calendarSubjectSettings.autoselectedPriceListsItemsIds[
                                mealType.id
                              ]
                            "
                            :value="portionSize.priceListItem.id"
                            :unchecked-value="null"
                            :disabled="isRegister ? mealType.blockOnRegistration : false"
                          >
                            {{ portionSize.name }}

                            <HiddenPriceListItemBadge
                              v-if="shouldShowHiddenPriceListItemBadge(portionSize.priceListItem)"
                              :price-list-item="portionSize.priceListItem"
                            />
                          </b-form-checkbox>
                        </td>

                        <td class="ps-5">
                          <strong>{{ formatMoney(portionSize.priceListItem.price) }}</strong>
                        </td>
                      </tr>
                    </template>
                  </template>
                </template>
              </table>
            </div>
          </template>

          <b-alert v-else :model-value="true" variant="warning">
            {{
              $t(
                "First select institution, classroom and diet to see list of autoselected meal types.",
              )
            }}
          </b-alert>
        </b-form-group>
      </b-collapse>
    </div>

    <!-- Actions -->
    <FormActions
      :submit-action="onSubmit"
      :back-action="onGoBackButtonClick"
      :is-edit="isEditMode"
      :show-back-button="variant === 'default'"
      :custom-submit-text="isEditMode ? 'Save' : 'Create'"
      :custom-submit-icon="isEditMode ? 'fa-save' : 'fa-plus'"
      :justify-content-class="variant === 'default' ? 'between' : 'end'"
      :ready="isChildDataReady"
    />

    <AutomarkingInfoModal
      :show="showAutomarkingInfoModal"
      :variant="isRegister ? 'registration' : 'edit'"
      @hide="onAutomarkingInfoModalHide"
    />

    <AutomarkingActivationConfirmModal
      v-if="showAutomarkingActivationConfirmModal"
      @confirm="onAutomarkingActivationConfirmed"
      @cancel="onAutomarkingActivationCancelled"
      @show-info-click="onHowAutomarkingWorksClick"
    />

    <CalendarSubjectSettingsLogModal
      :show="showCalendarSubjectSettingsLogModal"
      :calendar-subject-settings-logs="calendarSubjectSettingsLogs"
      @hide="onCalendarSubjectSettingsLogModalHide"
    />
  </b-form>
</template>

<script>
import Multiselector from "@/components/Multiselector.vue";
import getCurrentDate from "@/utils/getCurrentDate";
import { IS_DEVELOP, IS_RPK, IS_ZSP21, IS_POLKA } from "@/config";
import { AUTOMARK_DEFAULT_START_DATE, AUTOMARK_DEFAULT_END_DATE } from "@/config/automarkDefaults";
import AutomarkingInfoModal from "../../calendar/components/AutomarkingInfoModal.vue";
import AutomarkingInfoButton from "../../calendar/components/AutomarkingInfoButton.vue";
import AutomarkingActivationConfirmModal from "../../calendar/components/AutomarkingActivationConfirmModal.vue";
import { shouldConfirmAutomarkingActivation } from "@/modules/calendar/composables/useAutomarkingActivationConfirmation";
import DietsSelect from "@/modules/diets/components/DietsSelect.vue";
import { mapGetters, mapMutations } from "vuex";
import { formatMoney } from "@/utils/money";
import Tooltip from "@/components/Tooltip.vue";
import { useModalController } from "@/components/newUi/modals/useModalController";
import ChildrenGroupsSelect from "@/modules/childrenGroups/components/ChildrenGroupsSelect.vue";
import FormActions from "@/components/Form/FormActions.vue";
import HiddenPriceListItemBadge from "@/modules/priceLists/components/HiddenPriceListItemBadge.vue";
import CalendarSubjectSettingsLogModal from "@/modules/calendar/components/CalendarSubjectSettingsLogModal.vue";
import InstitutionsHideableSelect from "@/modules/institutions/components/InstitutionsHideableSelect.vue";
import RegistrationInstitutionSelect from "@/modules/institutions/components/RegistrationInstitutionSelect.vue";
import {
  addCardsFee,
  firstCardFee,
  nextCardFee,
  allowChangingDiets,
  allowChangingClassrooms,
  allowChangingInstitutions,
  allowChangingAllergens,
  allowChangingAllergensNote,
  prepaidOrdersAllow,
  allowSubscribedPayments,
  cardNumberLength,
} from "@/modules/settings/queries/tenantSettingsSelectors";
import { cardNumberPattern, isValidCardNumber, normalizeCardNumber } from "@/utils/cardNumber";
import { useCanEditDiningCard } from "@/modules/settings/composables/useCanEditDiningCard";
import { computed, useId } from "vue";
import { useStore as useVuexStore } from "vuex";
import { History } from "lucide-vue-next";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";
import AutomarkingMealTypesPicker from "@/components/newUi/calendar/AutomarkingMealTypesPicker.vue";

export default {
  name: "ParentChildEditForm",

  components: {
    InstitutionsHideableSelect,
    RegistrationInstitutionSelect,
    HiddenPriceListItemBadge,
    FormActions,
    AutomarkingInfoModal,
    AutomarkingInfoButton,
    AutomarkingActivationConfirmModal,
    Multiselector,
    DietsSelect,
    Tooltip,
    ChildrenGroupsSelect,
    CalendarSubjectSettingsLogModal,
    AutomarkingMealTypesPicker,
    HistoryIcon: History,
  },

  props: {
    variant: {
      type: String,
      default: "default",
      required: false,
    },
    child: {
      type: Object,
      default: null,
    },
    districts: {
      type: Array,
      default: null,
    },
    classrooms: {
      type: Array,
      default: null,
    },
    allergens: {
      type: Array,
      default: null,
    },
    priceListsGroups: {
      type: Array,
      default: null,
    },
    isRegister: {
      type: Boolean,
      default: false,
    },
    selectedChildData: {
      type: Object,
      default: null,
    },
    submit: {
      type: Function,
      required: true,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    defaultInstitutionId: {
      type: Number,
      default: null,
    },
    // name to show as the locked/readonly institution when defaultInstitutionId is set
    defaultInstitutionName: {
      type: String,
      default: null,
    },
  },

  emits: ["institutionChange", "goBack", "ready"],

  setup() {
    const { confirm } = useModalController();
    const store = useVuexStore();
    const canEditDiningCard = useCanEditDiningCard();
    const canChangeAllergens = computed(() => {
      if (!!store.getters["auth/canEditChildren"] && !store.getters["auth/isParent"]) return true;
      return allowChangingAllergens.value;
    });
    const canChangeAllergensNote = computed(() => {
      if (!!store.getters["auth/canEditChildren"] && !store.getters["auth/isParent"]) return true;
      return allowChangingAllergensNote.value;
    });
    return {
      confirm,
      newUiEnabled: useNewUiEnabled(),
      formId: `recipient-form-${useId()}`,
      allowChangingDiets,
      allowChangingClassrooms,
      allowChangingInstitutions,
      allowSubscribedPayments,
      cardNumberLength,
      canEditDiningCard,
      canChangeAllergens,
      canChangeAllergensNote,
      addCardsFee,
      firstCardFee,
      nextCardFee,
      allowPrepaidOrders: prepaidOrdersAllow,
    };
  },

  data() {
    return {
      initializingChild: false,
      isChildDataReady: false,
      childData: {
        firstName: null,
        lastName: null,
        cardNumber: null,
        classroomId: null,
        institutionId: null,
        dietId: null,
        allergies: [],
        allergiesNote: null,
        generalNote: null,
        hasDiscount: null,
        discountPercent: null,
        expirationDate: null,
        calendarSubjectSettings: {
          autoselectedMealTypes: [],
          autoselectedPriceListsItemsIds: {},
          automarkedWeekdays: {
            onMonday: true,
            onTuesday: true,
            onWednesday: true,
            onThursday: true,
            onFriday: true,
            onSaturday: false,
            onSunday: false,
          },
          automarkPresences: true,
          // TODO: Refactor to use proper dates
          automarkStartDate: AUTOMARK_DEFAULT_START_DATE,
          automarkEndDate: AUTOMARK_DEFAULT_END_DATE,
          autoselectionInterval: "one_month",
        },
        priceListsGroupId: null,
        subscribedPayments: false,
        internalNote: null,
        subjectNotificationsSettings: {
          missingOrdersEnabled: false,
          missingOrdersDaysBefore: 1,
          missingOrdersTriggerTime: "12:00",
        },
        childGroupId: null,
        selectedDistrictId: null,
      },
      selectedInstitution: this.child?.institution?.id ?? null,
      newInstitutionId: null,
      registerDietsOptions: [],
      orderingPrepaids: false,
      showAutomarkingInfoModal: false,
      showAutomarkingActivationConfirmModal: false,
      showCalendarSubjectSettingsLogModal: false,
      itemsGroupedByMealTypeCategories: [],
      calendarSubjectSettingsLogs: [],
      pendingAutoselectedPriceListItemIdsArray: null,
      fieldErrors: {},
    };
  },

  computed: {
    ...mapGetters({
      isAdmin: "auth/isAdmin",
      isInstitutionAdmin: "auth/isInstitutionsAdmin",
      isParent: "auth/isParent",
      isInstitutionsAdmin: "auth/isInstitutionsAdmin",
    }),
    newUiLayout() {
      return this.newUiEnabled && !this.isRegister;
    },

    cardNumberInputPattern() {
      return `^$|${cardNumberPattern(this.cardNumberLength)}`;
    },

    // needed by InstitutionsHideableSelect
    mountMode() {
      if (!this.child) {
        return "create";
      }
      return "edit";
    },
    canSeeDiscount() {
      if (this.isAdmin) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },

    canChangeAllergensForCurrentDiet() {
      if (!this.childData.dietId) {
        return false;
      }

      if (this.isAdmin) {
        return true;
      }

      // Register mode uses the local registerDietsOptions, not the diets/options store.
      if (this.isRegister) {
        const diet = this.registerDietsOptions.find(
          (option) => option.value === this.childData.dietId,
        );

        return !!diet?.allowChangingAllergens;
      }

      return this.$store.getters["diets/canChangeAllergens"](this.childData.dietId);
    },

    canChangeDiet() {
      if (!this.child) return true;
      if (this.isAdmin) return true;
      const canEditChildren = !!this.$store.getters["auth/canEditChildren"];
      if (canEditChildren && !this.isParent) return true;
      return this.allowChangingDiets;
    },

    canChangeClassroom() {
      if (!this.child) return true;
      if (this.isAdmin) return true;
      const canEditChildren = !!this.$store.getters["auth/canEditChildren"];
      if (canEditChildren && !this.isParent) return true;
      return this.allowChangingClassrooms;
    },

    canChangeInstitution() {
      if (this.isAdmin) return true;
      const canEditChildren = !!this.$store.getters["auth/canEditChildren"];
      if (canEditChildren && !this.isParent) return true;
      return this.allowChangingInstitutions;
    },

    showAutomarking() {
      if (this.isRegister) {
        return true;
      }

      if (IS_ZSP21) {
        return this.isAdmin;
      }

      if (this.isAdmin || this.isParent) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },

    enableSubscribedPaymentsField() {
      if (!this.allowSubscribedPayments) {
        return false;
      }

      if (this.isAdmin) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },

    canSeePrepaidsOptions() {
      if (!this.allowPrepaidOrders) {
        return false;
      }

      if (this.isAdmin) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },

    canEditSubjectNotificationsSettings() {
      if (this.isAdmin || this.isParent) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },

    allergensOptions() {
      if (this.isRegister) {
        return this.allergens;
      }

      const options = [];

      for (const allergen of this.allergens) {
        options.push({ value: allergen.id, text: allergen.name });
      }

      return options;
    },

    classroomOptions() {
      if (this.isRegister) {
        return this.classrooms;
      }

      const options = [];

      for (const classroom of this.classrooms) {
        const text =
          this.isAdmin || this.isInstitutionsAdmin
            ? `${classroom.name} (${classroom.institution.name})`
            : classroom.name;

        options.push({
          value: classroom.id,
          text: text,
          name: classroom.name,
          institutionId: classroom.institution.id,
          hasDefaultPriceListsGroupForMembers: classroom.hasDefaultPriceListsGroupForMembers,
          relatedPriceListsGroupId: classroom.relatedPriceListsGroup
            ? classroom.relatedPriceListsGroup.id
            : null,
        });
      }

      return options;
    },

    isEditMode() {
      return this.child || (this.isRegister && this.selectedChildData);
    },

    canModifyPriceListsGroup() {
      if (this.isAdmin) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },

    showNote() {
      if (IS_DEVELOP) {
        return true;
      }

      const selectedClassroomOption = this.classroomOptions.find(
        (option) => option.value === this.childData.classroomId,
      );

      // Show only for "Obiad do domu" institution in RPK
      if (IS_RPK && selectedClassroomOption && selectedClassroomOption.institutionId === 79) {
        return true;
      }

      return false;
    },

    defaultAllergensValues() {
      return this.child
        ? this.child.allergies.map((allergen) => allergen.id)
        : this.childData.allergies;
    },

    hasDistricts() {
      return this.$store.getters["districts/hasOptions"] || this.districts?.length > 0;
    },

    selectedDietDescription() {
      const dietId = this.childData.dietId;

      if (!dietId) {
        return null;
      }

      // See canChangeAllergensForCurrentDiet for why register mode reads its own local options.
      const options = this.isRegister ? this.registerDietsOptions : this.$store.state.diets.options;
      const diet = options.find((diet) => diet.value === dietId);

      return diet ? diet.description : null;
    },

    showExtraAllergensInfo() {
      return IS_POLKA;
    },

    autoselectionIntervalOptions() {
      if (IS_ZSP21) {
        return [{ value: "one_month", text: this.$t("interval_one_month") }];
      }

      return [
        { value: "one_week", text: this.$t("interval_one_week") },
        { value: "one_month", text: this.$t("interval_one_month") },
      ];
    },

    priceListsGroupsOptions() {
      const options = [];

      for (const priceListsGroup of this.priceListsGroups) {
        options.push({
          value: priceListsGroup.id,
          text: priceListsGroup.name,
          defaultForChildren: priceListsGroup.defaultForChildren,
          defaultForInstitutionsWorkers: priceListsGroup.defaultForInstitutionsWorkers,
        });

        if (priceListsGroup.defaultForChildren && this.childData.priceListsGroupId === null) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.childData.priceListsGroupId = priceListsGroup.id;
        }
      }

      return options;
    },

    isRegistration() {
      return (
        this.$store.getters["auth/user"] === null ||
        (this.$store.getters["auth/isParent"] && this.child === null)
      );
    },

    showGroups() {
      if (this.isRegistration || this.$store.getters["auth/isParent"]) {
        return false;
      }

      if (this.isAdmin) {
        return true;
      }

      if (
        this.isInstitutionsAdmin &&
        this.$store.getters["auth/permissions"].includes("children_edit")
      ) {
        return true;
      }

      return false;
    },
  },

  watch: {
    selectedChildData: {
      handler: function (newValue) {
        if (newValue) {
          this.childData = JSON.parse(JSON.stringify(newValue));
          this.selectedInstitution = this.childData.institutionId;

          this.pendingAutoselectedPriceListItemIdsArray = null;

          // re-editing an already-added child: autoselectedPriceListsItemsIds was flattened to an array on the previous submit, reconstruct the map once meal type categories reload
          if (
            Array.isArray(this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds)
          ) {
            this.pendingAutoselectedPriceListItemIdsArray =
              this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds;
            this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds = {};
          }
        }
      },
      deep: true,
      immediate: true,
    },

    itemsGroupedByMealTypeCategories(newValue) {
      if (this.pendingAutoselectedPriceListItemIdsArray && newValue.length > 0) {
        this.applyPendingAutoselectedPriceListItemIdsArray();
      }
    },

    "childData.dietId"() {
      if (this.initializingChild) return;

      this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds = {};
      this.itemsGroupedByMealTypeCategories = [];

      this.getMealTypeCategories();
    },

    selectedInstitution: {
      handler(newValue) {
        if (this.isRegister) {
          this.fetchRegisterDietsOptions(newValue);
        }

        if (this.initializingChild) return;

        this.getMealTypeCategories();
      },
      immediate: true,
    },

    "childData.priceListsGroupId"() {
      if (this.initializingChild) return;

      this.getMealTypeCategories();
    },

    isVisible: {
      handler: function (newValue) {
        if (newValue === false && !this.child) {
          this.resetForm();
        }

        // Pre-select institution when modal opens (only for new children, not editing). Goes
        // through onInstitutionChange (not a bare assignment) so childData.institutionId is set
        // and the institutionChange event fires, filtering the classroom list right away instead
        // of only after the user touches the institution select.
        if (newValue === true && !this.selectedChildData && this.defaultInstitutionId) {
          this.onInstitutionChange(this.defaultInstitutionId);
          this.selectedInstitution = this.defaultInstitutionId;
        }
      },
      immediate: true,
    },
  },

  async mounted() {
    this.setDefaultAutomarkPresencesConfig();

    try {
      if (this.child) {
        await this.setChildData(this.child);
      }
    } finally {
      this.isChildDataReady = true;

      this.$emit("ready");
    }
  },

  methods: {
    ...mapMutations({
      showNotification: "notifications/showNotification",
    }),

    formatMoney,
    getCurrentDate,

    shouldShowHiddenPriceListItemBadge(priceListItem) {
      if (!priceListItem || !priceListItem.hidden) {
        return false;
      }

      if (priceListItem.hiddenFor === "parents" && !this.$store.getters["auth/isParent"]) {
        return false;
      }

      if (!priceListItem.hiddenWeekdays || priceListItem.hiddenWeekdays.length === 0) {
        return false;
      }

      return true;
    },

    onChangeSelectedAllergens(allergens) {
      this.childData.allergies = allergens;
    },

    async checkIfChildHasFutureOrdersThatWillBeCancelled() {
      if (this.isRegister) {
        return;
      }

      const result = await this.$store.dispatch("orders/countSubjectOrders", {
        subjectType: "child",
        subjectId: this.child.id,
        criteria: {
          childId: this.child.id,
          dateFrom: this.getCurrentDate(),
          status: "active",
        },
      });

      if (result > 0) {
        const confirmed = await this.confirm({
          props: {
            body: this.$t(
              'Are you sure that you want to change child\'s institution? There are "{ordersCount}" active, future orders that will be cancelled if you confirm.',
              { ordersCount: result },
            ),
            title: this.$t("Confirm removal"),
            okVariant: "danger",
            okTitle: this.$t("Confirm"),
            cancelTitle: this.$t("Cancel"),
            headerBgVariant: "danger",
            headerCloseClass: "d-hide",
          },
        });

        if (!confirmed) {
          return false;
        }
      }

      return true;
    },

    async validateForm() {
      if (!this.validateCardNumber()) {
        return false;
      }

      if (!this.isRegister && this.child && this.child.institution?.id !== this.newInstitutionId) {
        const userHasConfirmedThatOrdersAreGonnaBeCancelled =
          await this.checkIfChildHasFutureOrdersThatWillBeCancelled();

        if (!userHasConfirmedThatOrdersAreGonnaBeCancelled) {
          return false;
        }
      }

      if (!this.selectedInstitution) {
        this.showNotification({
          type: "danger",
          text: this.$t("The child must be assigned to an institution."),
        });

        return false;
      }

      if (!this.childData.classroomId) {
        this.showNotification({
          type: "danger",
          text: this.$t("The child must be assigned to classroom."),
        });

        return false;
      }

      if (!this.childData.dietId) {
        this.showNotification({
          type: "danger",
          text: this.$t("The child must have a diet selected."),
        });

        return false;
      }

      return true;
    },

    async onSubmit() {
      if (!(await this.validateForm())) {
        return;
      }

      if (
        shouldConfirmAutomarkingActivation(
          this.child?.calendarSubjectSettings ?? null,
          this.childData.calendarSubjectSettings,
        )
      ) {
        this.showAutomarkingActivationConfirmModal = true;

        return;
      }

      await this.performSubmit();
    },

    onAutomarkingActivationConfirmed() {
      this.showAutomarkingActivationConfirmModal = false;

      this.performSubmit();
    },

    onAutomarkingActivationCancelled() {
      this.showAutomarkingActivationConfirmModal = false;
    },

    async performSubmit() {
      if (
        !this.canChangeAllergensForCurrentDiet &&
        (this.childData.allergies.length > 0 ||
          (typeof this.childData.allergiesNote === "string" &&
            this.childData.allergiesNote.trim().length > 0))
      ) {
        this.clearAllergensData();
      }

      if (this.isRegister && this.childData.calendarSubjectSettings.automarkPresences) {
        this.automarkBlockedOnRegistrationMealTypes();
      }

      this.fieldErrors = {};

      const copyOfChildData = JSON.parse(JSON.stringify(this.childData));

      copyOfChildData.cardNumber = normalizeCardNumber(copyOfChildData.cardNumber);

      // filter out null values from autoselectedPriceListsItemsIds
      copyOfChildData.calendarSubjectSettings.autoselectedPriceListsItemsIds = Object.values(
        copyOfChildData.calendarSubjectSettings.autoselectedPriceListsItemsIds,
      ).filter((item) => item !== null);

      try {
        await this.submit(copyOfChildData);
      } catch (error) {
        this.handleSubmitError(error);

        return;
      }

      this.resetForm();
    },

    resetForm() {
      this.childData = {
        firstName: null,
        lastName: null,
        cardNumber: null,
        classroomId: null,
        institutionId: null,
        dietId: null,
        allergies: [],
        allergiesNote: null,
        generalNote: null,
        hasDiscount: null,
        discountPercent: null,
        expirationDate: null,
        calendarSubjectSettings: {
          autoselectedMealTypes: [],
          autoselectedPriceListsItemsIds: {},
          automarkedWeekdays: {
            onMonday: true,
            onTuesday: true,
            onWednesday: true,
            onThursday: true,
            onFriday: true,
            onSaturday: false,
            onSunday: false,
          },
          automarkPresences: true,
          // TODO: Refactor to use proper dates
          automarkStartDate: AUTOMARK_DEFAULT_START_DATE,
          automarkEndDate: AUTOMARK_DEFAULT_END_DATE,
          autoselectionInterval: "one_month",
        },
        priceListsGroupId: null,
        subscribedPayments: false,
        internalNote: null,
        subjectNotificationsSettings: {
          missingOrdersEnabled: false,
          missingOrdersDaysBefore: 1,
          missingOrdersTriggerTime: "12:00",
        },
        childGroupId: null,
      };

      this.childData.selectedDistrictId = null;
      this.selectedInstitution = null;
      this.newInstitutionId = null;
      this.orderingPrepaids = false;
      this.showAutomarkingInfoModal = false;
      this.itemsGroupedByMealTypeCategories = [];
      this.fieldErrors = {};
    },

    handleSubmitError(error) {
      const validationErrors = error?.response?.status === 422 ? error.response.data?.errors : null;

      if (this.newUiLayout && validationErrors && typeof validationErrors === "object") {
        this.fieldErrors = validationErrors;

        this.$nextTick(() => this.focusFirstInvalidField());
      }

      // the axios interceptor has already shown the API error
      if (error?.isAxiosError && !error.config?.suppressErrorNotification) {
        return;
      }

      this.showNotification({
        type: "error",
        text: this.$t("An error occurred while saving the child.") + " " + error?.message,
      });
    },

    fieldError(path) {
      return this.fieldErrors[path] ?? undefined;
    },

    invalidAttr(path) {
      return this.fieldErrors[path] ? "true" : undefined;
    },

    clearFieldError(path) {
      if (!this.fieldErrors[path]) {
        return;
      }

      const remainingErrors = { ...this.fieldErrors };

      delete remainingErrors[path];

      this.fieldErrors = remainingErrors;
    },

    focusFirstInvalidField() {
      const invalidField = this.$el?.querySelector?.(
        '[aria-invalid="true"], .ui-field.is-invalid input',
      );

      if (!invalidField) {
        return;
      }

      invalidField.scrollIntoView({ block: "center", behavior: "smooth" });

      invalidField.focus({ preventScroll: true });
    },

    onClassroomSelect(classroomId) {
      this.clearFieldError("classroomId");

      this.onChangeClassroom(classroomId);
    },

    automarkBlockedOnRegistrationMealTypes() {
      const autoselectedPriceListsItemsIds =
        this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds;

      for (const category of Object.values(this.itemsGroupedByMealTypeCategories)) {
        for (const mealType of Object.values(category.mealTypes)) {
          if (mealType.blockOnRegistration) {
            let defaultPortionSize = Object.values(mealType.portionSizes).find(
              (portionSize) => portionSize.default,
            );

            if (!defaultPortionSize) {
              defaultPortionSize = Object.values(mealType.portionSizes)[0];
            }

            if (!defaultPortionSize || !defaultPortionSize.priceListItem) {
              continue;
            }

            // Checks only one portion size for this meal type (preferably the one with default portion size, otherwise the first one)
            autoselectedPriceListsItemsIds[mealType.id] = defaultPortionSize.priceListItem.id;
          }
        }
      }
    },

    clearAllergensData() {
      this.childData.allergies = [];
      this.childData.allergiesNote = null;
    },

    async fetchRegisterDietsOptions(institutionId) {
      if (!institutionId) {
        this.registerDietsOptions = [];
        return;
      }

      this.registerDietsOptions = await this.$store.dispatch("auth/getDietsRegisterOptions", {
        institutionId,
      });
    },

    clearInstitutionRelatedData() {
      this.selectedInstitution = null;
      this.childData.classroomId = null;
      this.childData.institutionId = null;
      this.childData.dietId = null;
      this.childData.priceListsGroupId = null;
      this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds = {};
      this.itemsGroupedByMealTypeCategories = [];
    },

    onInstitutionChange(institutionId) {
      if (institutionId === this.childData.institutionId) {
        return;
      }

      this.childData.classroomId = null;
      this.childData.institutionId = institutionId;
      this.childData.dietId = null;
      this.childData.priceListsGroupId = null;
      this.newInstitutionId = institutionId;
      this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds = {};
      this.itemsGroupedByMealTypeCategories = [];

      this.$emit("institutionChange", institutionId, this.childData.priceListsGroupId);
    },

    onGoBackButtonClick() {
      this.$emit("goBack");
    },

    async setChildData(child) {
      if (!this.child) return;

      this.initializingChild = true;
      const autoselectedPriceListsItemsIdsCopy =
        child.calendarSubjectSettings.autoselectedPriceListsItemsIds || {};

      this.onInstitutionChange(child.institution?.id);
      this.selectedInstitution = child.institution?.id;
      this.childData.dietId = child.relatedDietId;
      this.childData.priceListsGroupId = child.priceListsGroupId;
      this.childData.classroomId = child.classroom?.id;
      this.childData.selectedDistrictId = child.institution?.districtId;
      this.childData.firstName = child.fullName.firstName;
      this.childData.lastName = child.fullName.lastName;
      this.childData.cardNumber = child.cardNumber;

      try {
        await this.getMealTypeCategories();

        this.applyExistingAutoselections(autoselectedPriceListsItemsIdsCopy);
      } catch {
        // The request error is already shown as a toast; the rest of the child's data must still fill the form.
      } finally {
        this.initializingChild = false;
      }

      this.childData.dietId = child.relatedDietId;
      this.childData.allergies = Object.values(child.allergies || {}).map(
        (allergen) => allergen.id,
      );
      this.childData.allergiesNote = child.allergiesNote;
      this.childData.generalNote = child.generalNote;
      this.childData.hasDiscount = child.hasDiscount;
      this.childData.discountPercent = child.discountPercent;
      this.childData.expirationDate = child.expirationDate;
      this.childData.calendarSubjectSettings.automarkPresences =
        child.calendarSubjectSettings.automarkPresences;
      this.childData.calendarSubjectSettings.automarkStartDate =
        child.calendarSubjectSettings.automarkStartDate;
      this.childData.calendarSubjectSettings.automarkEndDate =
        child.calendarSubjectSettings.automarkEndDate;
      this.childData.calendarSubjectSettings.automarkedWeekdays =
        child.calendarSubjectSettings.automarkedWeekdays;
      this.childData.calendarSubjectSettings.autoselectionInterval =
        child.calendarSubjectSettings.autoselectionInterval;
      this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds =
        child.calendarSubjectSettings.autoselectedPriceListsItemsIds;
      this.childData.orderingPrepaids = child.orderingPrepaids;
      this.childData.priceListsGroupId = child.priceListsGroupId;
      this.childData.subscribedPayments = child.subscribedPayments;
      this.childData.subjectNotificationsSettings.missingOrdersEnabled =
        child.subjectNotificationsSettings.missingOrdersEnabled;
      this.childData.subjectNotificationsSettings.missingOrdersDaysBefore =
        child.subjectNotificationsSettings.missingOrdersDaysBefore;
      this.childData.subjectNotificationsSettings.missingOrdersTriggerTime =
        child.subjectNotificationsSettings.missingOrdersTriggerTime;
      this.childData.childGroupId = child.childGroup?.id;

      if (this.isAdmin && Object.prototype.hasOwnProperty.call(child, "internalNote")) {
        this.childData.internalNote = child.internalNote;
      }

      if (
        (this.isAdmin || this.isInstitutionsAdmin) &&
        Array.isArray(child.calendarSubjectSettingsLogs ?? null)
      ) {
        this.calendarSubjectSettingsLogs = child.calendarSubjectSettingsLogs;
      }
    },

    applyExistingAutoselections(autoselectedPriceListsItemsIdsCopy) {
      for (const [mealTypeId, priceListItemId] of Object.entries(
        autoselectedPriceListsItemsIdsCopy,
      )) {
        this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds[mealTypeId] =
          priceListItemId;
      }
    },

    // rebuilds the mealTypeId -> priceListItemId map from a flattened priceListItemId array by matching ids against the loaded meal type categories
    applyPendingAutoselectedPriceListItemIdsArray() {
      const priceListItemIds = this.pendingAutoselectedPriceListItemIdsArray;
      this.pendingAutoselectedPriceListItemIdsArray = null;

      for (const category of this.itemsGroupedByMealTypeCategories) {
        for (const mealType of category.mealTypes) {
          for (const portionSize of Object.values(mealType.portionSizes)) {
            if (
              portionSize.priceListItem &&
              priceListItemIds.includes(portionSize.priceListItem.id)
            ) {
              this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds[mealType.id] =
                portionSize.priceListItem.id;
            }
          }
        }
      }
    },

    // TODO: Refactor to use proper dates
    setDefaultAutomarkPresencesConfig() {
      this.childData.calendarSubjectSettings.automarkPresences = true;
      this.childData.calendarSubjectSettings.automarkStartDate = AUTOMARK_DEFAULT_START_DATE;
      this.childData.calendarSubjectSettings.automarkEndDate = AUTOMARK_DEFAULT_END_DATE;
    },

    onChangeClassroom(classroomId) {
      this.childData.classroomId = classroomId;

      const selectedOption = this.classroomOptions.find((option) => option.value === classroomId);

      if (!selectedOption) {
        return;
      }

      if (
        selectedOption.hasDefaultPriceListsGroupForMembers &&
        selectedOption.relatedPriceListsGroupId
      ) {
        this.childData.priceListsGroupId = selectedOption.relatedPriceListsGroupId;
      } else {
        // may be empty during anonymous registration; getMealTypeCategories() falls back to classroomId
        const defaultPriceListsGroupOption = this.priceListsGroupsOptions.find(
          (option) => option.defaultForChildren === true,
        );

        this.childData.priceListsGroupId = defaultPriceListsGroupOption
          ? defaultPriceListsGroupOption.value
          : null;
      }

      this.$emit("institutionChange", this.selectedInstitution, this.childData.priceListsGroupId);

      if (this.selectedInstitution && this.childData.dietId) {
        this.getMealTypeCategories();
      }
    },

    async getMealTypeCategories() {
      if (!this.selectedInstitution || !this.childData.dietId) {
        return;
      }

      if (!this.childData.priceListsGroupId && !this.childData.classroomId) {
        return;
      }

      this.itemsGroupedByMealTypeCategories = await this.$store.dispatch(
        "priceLists/getItemsGroupedByMealTypeCategories",
        {
          institutionId: this.selectedInstitution,
          dietId: this.childData.dietId,
          priceListsGroupId: this.childData.priceListsGroupId,
          // fallback for when priceListsGroupId couldn't be resolved client-side
          classroomId: this.childData.priceListsGroupId ? null : this.childData.classroomId,
          criteria: {
            withoutHiddenOnRegistration: this.isRegister,
          },
        },
      );

      if (this.isRegister && this.childData.calendarSubjectSettings.automarkPresences) {
        this.automarkBlockedOnRegistrationMealTypes();
      }

      // this.childData.calendarSubjectSettings.autoselectedMealTypes = this.child
      //   ? this.child.calendarSubjectSettings.autoselectedPriceListsItemsIds
      //   : [];
    },

    onPriceListsGroupChange(priceListsGroupId) {
      this.childData.priceListsGroupId = priceListsGroupId;

      this.getMealTypeCategories();

      this.$emit("institutionChange", this.selectedInstitution, priceListsGroupId);
    },

    validateCardNumber() {
      if (!this.canEditDiningCard) {
        return true;
      }

      if (isValidCardNumber(this.childData.cardNumber, this.cardNumberLength)) {
        return true;
      }

      const message = this.$t(
        "Card number should have {cardNumberLength} characters and contain only letters or digits.",
        { cardNumberLength: this.cardNumberLength },
      );

      if (this.newUiLayout) {
        this.fieldErrors = { ...this.fieldErrors, cardNumber: message };

        this.$nextTick(() => this.focusFirstInvalidField());

        return false;
      }

      this.showNotification({ type: "danger", text: message });

      return false;
    },

    onHowAutomarkingWorksClick() {
      this.showAutomarkingInfoModal = true;
    },

    onAutomarkingInfoModalHide() {
      this.showAutomarkingInfoModal = false;
    },

    onCalendarSubjectSettingsHistoryClick() {
      this.showCalendarSubjectSettingsLogModal = true;
    },

    onCalendarSubjectSettingsLogModalHide() {
      this.showCalendarSubjectSettingsLogModal = false;
    },

    onAutoselectedMealTypeSelect(mealTypeId, priceListItemId) {
      this.childData.calendarSubjectSettings.autoselectedPriceListsItemsIds[mealTypeId] =
        priceListItemId;
    },
  },
};
</script>
<style lang="scss">
#parent-child-edit-form {
  input[disabled],
  input:disabled,
  .form-control[disabled],
  .form-control:disabled {
    opacity: 0.5;
  }
}
</style>
